/* eslint-disable func-style */
import React, { useMemo, useState, useCallback } from 'react';
import { QRCodeSVG as QRCode } from 'qrcode.react';
import {
  Header,
  Button,
  Input,
  Container,
  Breadcrumb,
  Segment,
  Grid,
  List,
} from 'semantic-ui-react';

const EDGE_INSTALL_URL = `${window.location.origin}/${currentCustomer}/edge_install`;
const NODE_URL = `${window.location.origin}/${currentCustomer}/nodes`;

function withSegment(Component) {
  return function WithSegmentComponent({ ...props }) {
    return (
      <Segment
        vertical
        style={{ width: '100%' }}
      >
        <Component {...props} />
      </Segment>
    );
  };
}

function ResultHeader({ title, action }) {
  const GridWithSegment = withSegment(Grid);

  return (
    <GridWithSegment
      className='no-print'
      verticalAlign='middle'
    >
      <Grid.Column
        floated='left'
        width={5}
      >
        <Header as='h3'>{title}</Header>
      </Grid.Column>
      <Grid.Column
        textAlign='right'
        width={5}
      >
        {action}
      </Grid.Column>
    </GridWithSegment>
  );
}

function Results({
  results,
  container = React.Fragment,
  children,
  noResultMessage = 'No results found',
  ...props
}) {
  const ContainerComponent = container;
  let items = [];

  if (results.length <= 0) {
    return noResultMessage;
  }

  for (let i = 0; i < results.length; i++) {
    items.push(children(results[i], i));
  }

  return <ContainerComponent {...props}>{items}</ContainerComponent>;
}

const ResultContainer = withSegment(Results);

function InactiveResultSection({ results }) {
  return (
    <Grid.Row className='no-print'>
      <ResultHeader title='Nodes with Inactive URL' />

      <ResultContainer
        bulleted
        horizontal
        link
        container={List}
        results={results}
      >
        {(hostname) => (
          <List.Item
            key={hostname}
            as='a'
            href={`${NODE_URL}/${hostname}`}
          >
            {hostname}
          </List.Item>
        )}
      </ResultContainer>
    </Grid.Row>
  );
}

function ActiveResultSection({ results }) {
  return (
    <Grid.Row>
      <ResultHeader
        // eslint-disable-next-line dom-in-jsx/no-window-calls
        action={
          <Button
            content='Print'
            icon='print'
            onClick={() => window.print()}
          />
        }
        title='Nodes with Active URL'
      />

      <ResultContainer
        stackable
        columns={2}
        container={Grid}
        divided='vertically'
        results={results}
      >
        {(resultGroup, idx) => (
          <ActiveResultGroup
            key={idx}
            group={resultGroup}
          />
        )}
      </ResultContainer>
    </Grid.Row>
  );
}

export default function QRCodePage(props) {
  const [search, setSearch] = useState(props.search || '');
  const [active, setActive] = useState([...props.active]);
  const [inactive, setInactive] = useState([...props.inactive]);

  const path = useMemo(() => {
    const storeList =
      typeof search === 'string'
        ? search
            .trim()
            .split(/[\s,]/)
            .filter((s) => s.match(/^\d+$/))
        : search;
    const params = (list) => {
      if (!Array.isArray(list)) {
        list = [list];
      }

      return list.map((store) => `store_numbers[]=${store}`).join('&');
    };

    return `${props.batchLink}?${params(storeList)}`;
  }, [search, props.batchLink]);

  const onConfirm = useCallback(() => {
    fetch(path, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setActive([...data.active]);
        setInactive([...data.inactive]);
      });
  }, [path]);

  return (
    <Container fluid>
      <Grid padded>
        <Grid.Row className='no-print'>
          <Header as='h1'>
            QR Code Generator
            <Header.Subheader>
              <Breadcrumb>
                <Breadcrumb.Section href={`/${currentCustomer}/nodes`}>
                  Nodes
                </Breadcrumb.Section>
                <Breadcrumb.Divider icon='right angle' />
                <Breadcrumb.Section active>
                  QR Code Generator
                </Breadcrumb.Section>
              </Breadcrumb>
            </Header.Subheader>
          </Header>
        </Grid.Row>

        <Grid.Row
          className='no-print'
          columns={1}
        >
          <em style={{ marginBottom: '10px' }}>
            Search for node(s) that you would like to generate and print QR
            codes.
          </em>
          <Input
            fluid
            action={
              <Button
                primary
                onClick={onConfirm}
              >
                Generate QR Codes
              </Button>
            }
            className='qr-code-search'
            defaultValue={search}
            placeholder='Search...'
            size='large'
            onChange={(e) => setSearch(e.target.value)}
          />
          <em>Please limit your search to 1,000 stores at a time.</em>
        </Grid.Row>

        <InactiveResultSection results={inactive} />

        <ActiveResultSection results={active} />
      </Grid>
    </Container>
  );
}

function ActiveResultGroup({ group }) {
  return (
    <Grid.Row className='pagebreak nodes-cluster'>
      {group.map(
        ({
          uuid,
          hostname,
          serialno: serialNumber,
          store_number: storeNumber,
        }) => (
          <ActiveResultItem
            key={uuid}
            hostname={hostname}
            serialNumber={serialNumber}
            storeNumber={storeNumber}
            uuid={uuid}
          />
        ),
      )}
    </Grid.Row>
  );
}

function ActiveResultItem({ uuid, hostname, serialNumber, storeNumber }) {
  const codeURL = `${EDGE_INSTALL_URL}/${uuid}`;

  return (
    <Grid.Column>
      <Grid columns={2}>
        <Grid.Column width={5}>
          <QRCode value={codeURL} />
        </Grid.Column>
        <Grid.Column>
          <Header>Hostname: {hostname}</Header>
          <Container>
            Node Number: {storeNumber} <br />
            Serial Number: {serialNumber} <br />
          </Container>
        </Grid.Column>
      </Grid>
    </Grid.Column>
  );
}
